import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Modal, Radio, Space, Switch, Table, message } from "antd";
import PageTitle from "../../components/PageTitle";
import {
  adminLogin,
  createSpecialFeeUiVip,
  deleteSpecialFeeUiVip,
  getSpecialFeeUiVipHouses,
  getSpecialFeeUiVipList,
  getUsers,
  patchSpecialFeeUiVip,
  patchSpecialFeeUiVipHouse,
} from "../../api";
import { setAdminAuthExtras } from "../../utils/adminPermissions";

function normalizePhone(v) {
  return String(v || "").replace(/\D/g, "").slice(0, 11);
}

function formatUserLabel(u) {
  const name = u?.nickname || u?.name || u?.real_name || "未设置昵称";
  return `${name}（ID:${u?.id}）`;
}

function formatUserSub(u) {
  const phone = u?.phone || u?.mobile || "—";
  const ut = u?.user_type != null ? `类型${u.user_type}` : "";
  return [phone, ut].filter(Boolean).join(" · ");
}

function extractUsersFromGetUsersRes(res) {
  if (!res?.success) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.list)) return res.data.list;
  if (Array.isArray(res.list)) return res.list;
  return [];
}

/**
 * VIP白名单：须名单∩人开关∩该房源开关，小程序才走新 UI（断网/失败默认旧版）。
 * 进页需再输一次当前管理员密码（防公用账号误点）；离开本页即失效（组件卸载清空）。
 */
const SpecialFeeUiVip = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [gatePassword, setGatePassword] = useState("");
  const [gateLoading, setGateLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [removeRow, setRemoveRow] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSearching, setPhoneSearching] = useState(false);
  const [phoneTip, setPhoneTip] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCandidates, setPickerCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [houseRow, setHouseRow] = useState(null);
  const [houseList, setHouseList] = useState([]);
  const [houseLoading, setHouseLoading] = useState(false);
  const [housePersonEnabled, setHousePersonEnabled] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const res = await getSpecialFeeUiVipList({
        page: p,
        page_size: ps,
        keyword: keyword || undefined,
      });
      if (res?.success) {
        setData(res.data?.list || []);
        setTotal(Number(res.data?.total || 0));
      } else {
        message.error(res?.error || "加载失败");
      }
    } catch {
      message.error("加载 VIP 白名单失败");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword]);

  useEffect(() => {
    if (!unlocked) return;
    load(page, pageSize);
  }, [unlocked, load, page, pageSize]);

  const handleGateOk = async () => {
    const username = String(localStorage.getItem("admin_username") || "").trim();
    const password = String(gatePassword || "");
    if (!username) {
      message.error("未找到当前管理员账号，请重新登录");
      return;
    }
    if (!password) {
      message.error("请输入密码");
      return;
    }
    setGateLoading(true);
    try {
      const res = await adminLogin({ username, password });
      if (res && res.success === true) {
        if (res.token) localStorage.setItem("admin_token", res.token);
        if (res.user?.username) localStorage.setItem("admin_username", res.user.username);
        if (res.user) setAdminAuthExtras(res.user);
        setGatePassword("");
        setUnlocked(true);
        message.success("验证通过");
      } else {
        message.error(res?.error || "密码不正确");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "密码不正确";
      message.error(msg === "Network Error" ? "网络异常，请重试" : msg);
    } finally {
      setGateLoading(false);
    }
  };

  const doCreate = async (fangdongUserId) => {
    const values = form.getFieldsValue();
    const res = await createSpecialFeeUiVip({
      fangdong_user_id: Number(fangdongUserId),
      enabled: values.enabled ? 1 : 0,
      remark: values.remark || "",
    });
    if (!res?.success) throw new Error(res?.error || "添加失败");
    message.success(res.data?.already ? "已更新该房东白名单记录" : "已加入 VIP 白名单");
    setModalOpen(false);
    setPickerOpen(false);
    setPickerCandidates([]);
    setSelectedCandidateId(null);
    setPhoneInput("");
    setPhoneTip("");
    form.resetFields();
    load(page, pageSize);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      await doCreate(values.fangdong_user_id);
    } catch (e) {
      message.error(e?.message || "添加失败");
    }
  };

  const handlePhoneSearch = async () => {
    const phone = normalizePhone(phoneInput);
    if (phone.length !== 11) {
      setPhoneTip("请输入 11 位手机号");
      return;
    }
    setPhoneSearching(true);
    setPhoneTip("");
    try {
      const res = await getUsers({ page: 1, page_size: 100, keyword: phone });
      const users = extractUsersFromGetUsersRes(res);
      const matched = users.filter((u) => {
        const p = normalizePhone(u.phone || u.mobile);
        return p === phone;
      });
      if (matched.length === 0) {
        setPhoneTip("未找到该手机号对应的用户，请先确认已注册");
        return;
      }
      if (matched.length === 1) {
        form.setFieldsValue({ fangdong_user_id: String(matched[0].id) });
        setPhoneTip(`已选中：${formatUserLabel(matched[0])} · ${formatUserSub(matched[0])}`);
        return;
      }
      setPickerCandidates(matched);
      setSelectedCandidateId(Number(matched[0].id));
      setPickerOpen(true);
      setPhoneTip(`该手机号对应 ${matched.length} 个账号，请选择`);
    } catch (e) {
      setPhoneTip(e?.message || "搜索用户失败");
    } finally {
      setPhoneSearching(false);
    }
  };

  const handleConfirmPicker = async () => {
    const picked = pickerCandidates.find((u) => Number(u.id) === Number(selectedCandidateId));
    if (!picked) {
      message.warning("请选择要加入的用户");
      return;
    }
    form.setFieldsValue({ fangdong_user_id: String(picked.id) });
    setPickerOpen(false);
    setPhoneTip(`已选中：${formatUserLabel(picked)} · ${formatUserSub(picked)}`);
    // 选完不自动提交，方便再改开关/备注后点确定
  };

  const handleToggle = async (row, checked) => {
    try {
      const res = await patchSpecialFeeUiVip(row.id, { enabled: checked ? 1 : 0 });
      if (!res?.success) throw new Error(res?.error || "更新失败");
      message.success(checked ? "已打开人开关" : "已关闭人开关");
      load(page, pageSize);
    } catch (e) {
      message.error(e?.message || "更新失败");
    }
  };

  const openHouses = async (row) => {
    setHouseRow(row);
    setHouseList([]);
    setHouseLoading(true);
    try {
      const res = await getSpecialFeeUiVipHouses(row.id);
      if (!res?.success) throw new Error(res?.error || "加载房源失败");
      setHouseList(res.data?.list || []);
      setHousePersonEnabled(!!res.data?.person_enabled);
    } catch (e) {
      message.error(e?.message || "加载房源失败");
      setHouseRow(null);
    } finally {
      setHouseLoading(false);
    }
  };

  const handleHouseToggle = async (house, checked) => {
    if (!houseRow?.id || !house?.id) return;
    try {
      const res = await patchSpecialFeeUiVipHouse(houseRow.id, house.id, {
        enabled: checked ? 1 : 0,
      });
      if (!res?.success) throw new Error(res?.error || "更新失败");
      setHouseList((prev) =>
        prev.map((h) =>
          Number(h.id) === Number(house.id)
            ? { ...h, special_fee_ui_enabled: checked ? 1 : 0 }
            : h
        )
      );
      load(page, pageSize);
      message.success(checked ? "已打开该房源" : "已关闭该房源");
    } catch (e) {
      message.error(e?.message || "更新房源开关失败");
    }
  };

  const handleRemoveOk = async () => {
    if (!removeRow?.id) return;
    setRemoveLoading(true);
    try {
      const res = await deleteSpecialFeeUiVip(removeRow.id);
      if (res?.success) {
        message.success("已移出白名单");
        setRemoveRow(null);
        load(page, pageSize);
      } else {
        message.error(res?.error || "删除失败");
      }
    } catch (e) {
      message.error(e?.response?.data?.error || e?.message || "删除失败");
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "房东用户ID", dataIndex: "fangdong_user_id", width: 120 },
    {
      title: "姓名",
      dataIndex: "real_name",
      width: 100,
      render: (v) => v || "—",
    },
    {
      title: "手机",
      dataIndex: "phone",
      width: 130,
      render: (v) => v || "—",
    },
    {
      title: "人开关",
      dataIndex: "enabled",
      width: 90,
      render: (v, row) => (
        <Switch
          checked={Number(v) === 1}
          onChange={(checked) => handleToggle(row, checked)}
        />
      ),
    },
    {
      title: "已开房源",
      width: 110,
      render: (_, row) =>
        `${Number(row.house_enabled_count || 0)}/${Number(row.house_total || 0)}`,
    },
    { title: "备注", dataIndex: "remark", ellipsis: true },
    {
      title: "时间",
      dataIndex: "created_at",
      width: 180,
      render: (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—"),
    },
    {
      title: "操作",
      width: 140,
      render: (_, row) => (
        <Space>
          <Button type="link" onClick={() => openHouses(row)}>
            房源
          </Button>
          <Button type="link" danger onClick={() => setRemoveRow(row)}>
            移出
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="VIP白名单" />
      <Modal
        title="进入 VIP白名单"
        open={!unlocked}
        closable={false}
        maskClosable={false}
        keyboard={false}
        confirmLoading={gateLoading}
        okText="验证进入"
        cancelText="返回"
        onOk={handleGateOk}
        onCancel={() => navigate("/system/roles/list")}
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: "#666" }}>
          本页影响租客费用展示，请再输入当前管理员密码。离开本页后需重新验证。
        </div>
        <Input.Password
          placeholder="管理员登录密码"
          value={gatePassword}
          onChange={(e) => setGatePassword(e.target.value)}
          onPressEnter={handleGateOk}
          autoFocus
        />
      </Modal>
      {!unlocked ? (
        <div style={{ color: "#999", padding: "24px 0" }}>验证通过后显示名单</div>
      ) : (
        <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="用户ID / 手机号 / 备注"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
          style={{ width: 240 }}
        />
        <Button
          type="primary"
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({ enabled: false });
            setPhoneInput("");
            setPhoneTip("");
            setModalOpen(true);
          }}
        >
          加入白名单
        </Button>
        <Button
          onClick={() => {
            setPage(1);
            load(1, pageSize);
          }}
        >
          查询
        </Button>
      </Space>
      <div style={{ marginBottom: 12, fontSize: 12, color: "#888" }}>
        「人开关」只表示业主有资格；还要在「房源」里逐栋打开（默认关），租客才会走新 UI。
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <Modal
        title="加入 VIP白名单"
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        okText="确定加入"
      >
        <Form form={form} layout="vertical" initialValues={{ enabled: false }}>
          <Form.Item label="方式一：手机号查找">
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="11 位手机号"
                value={phoneInput}
                maxLength={11}
                onChange={(e) => {
                  setPhoneInput(normalizePhone(e.target.value));
                  if (phoneTip) setPhoneTip("");
                }}
                onPressEnter={handlePhoneSearch}
              />
              <Button
                type="primary"
                loading={phoneSearching}
                disabled={normalizePhone(phoneInput).length !== 11}
                onClick={handlePhoneSearch}
              >
                查找
              </Button>
            </Space.Compact>
            {phoneTip ? (
              <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>{phoneTip}</div>
            ) : null}
          </Form.Item>

          <Form.Item
            name="fangdong_user_id"
            label="方式二：房东用户 ID（user.id）"
            rules={[{ required: true, message: "请填写用户ID，或先用手机号查找选中" }]}
          >
            <Input placeholder="例如 43619（手机号选中后会自动填入）" />
          </Form.Item>
          <Form.Item name="enabled" label="同时打开开关" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="可选" />
          </Form.Item>
          <div style={{ color: "#888", fontSize: 12 }}>
            须「白名单」且「人开关开」且「该房源开关开」；房源开关默认关，断网仍为旧版。
          </div>
        </Form>
      </Modal>

      <Modal
        title="选择要加入的用户"
        open={pickerOpen}
        onCancel={() => {
          setPickerOpen(false);
          setPickerCandidates([]);
          setSelectedCandidateId(null);
        }}
        onOk={handleConfirmPicker}
        okText="确认选择"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: "#666" }}>
          该手机号绑定了多个账号，请选择其中一个加入 VIP白名单：
        </div>
        <Radio.Group
          value={selectedCandidateId}
          onChange={(e) => setSelectedCandidateId(e.target.value)}
          style={{ width: "100%" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pickerCandidates.map((u) => (
              <Radio key={u.id} value={Number(u.id)} style={{ alignItems: "flex-start", margin: 0 }}>
                <div style={{ marginLeft: 4 }}>
                  <div style={{ fontWeight: 500, lineHeight: "20px" }}>{formatUserLabel(u)}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{formatUserSub(u)}</div>
                </div>
              </Radio>
            ))}
          </div>
        </Radio.Group>
      </Modal>

      <Modal
        title={`房源开关 · 房东 ${houseRow?.fangdong_user_id || ""}`}
        open={!!houseRow}
        onCancel={() => {
          setHouseRow(null);
          setHouseList([]);
        }}
        footer={null}
        width={640}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, fontSize: 12, color: "#888" }}>
          第三道闸默认关。人开关{housePersonEnabled ? "已开" : "未开"}
          ；人开关关着时，开房源也不会对租客生效。
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={houseLoading}
          pagination={false}
          dataSource={houseList}
          locale={{ emptyText: "该房东名下暂无房源" }}
          columns={[
            { title: "房源ID", dataIndex: "id", width: 90 },
            {
              title: "名称",
              dataIndex: "house_name",
              ellipsis: true,
              render: (v) => v || "—",
            },
            {
              title: "地址",
              dataIndex: "address",
              ellipsis: true,
              render: (v) => v || "—",
            },
            {
              title: "本栋开关",
              dataIndex: "special_fee_ui_enabled",
              width: 90,
              render: (v, row) => (
                <Switch
                  checked={Number(v) === 1}
                  onChange={(checked) => handleHouseToggle(row, checked)}
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title="移出白名单"
        open={!!removeRow}
        confirmLoading={removeLoading}
        onOk={handleRemoveOk}
        onCancel={() => setRemoveRow(null)}
        okButtonProps={{ danger: true }}
      >
        确认将房东用户 ID {removeRow?.fangdong_user_id} 移出 VIP白名单？
      </Modal>
        </>
      )}
    </div>
  );
};

export default SpecialFeeUiVip;
